import type {
  StreamingEventCollection,
  StreamingEvent,
} from '../types/chat.types';
import type {
  ContentProcessorOptions,
  ContentProcessorState,
  ContentSegment,
} from './contentProcessor.types';

import {
  isA2UIEvent,
  isArtifactEvent,
  isDebugEvent,
  isErrorEvent,
  isCanceledEvent,
  isImageEvent,
  isProgressEvent,
  isReferenceEvent,
  isReferenceGroupEvent,
  isReferenceStartEvent,
  isTextEvent,
  isToolUsageProgressEvent,
  isToolUsageStartEvent,
  isToolUsageStopEvent,
  isWarningEvent,
  type ChatMessage,
} from '../types/chat.types';
import {
  handleA2UIEvent,
  handleArtifactEvent,
  handleDebugEvent,
  handleErrorEvent,
  handleCanceledEvent,
  handleImageEvent,
  handleProgressEvent,
  handleReferenceStartEvent,
  handleReferenceGroupEvent,
  handleReferenceEvent,
  handleTextEvent,
  handleToolUsageStartEvent,
  handleToolUsageProgressEvent,
  handleToolUsageStopEvent,
  handleWarningEvent,
  flushAccumulatedRefs,
} from './eventHandlers';
import {
  addPreviousEventFormatting,
  preprocessContentNodes,
} from './finalization';
import { validateResult } from './validation';

/**
 * Initial state setup
 */

export function createInitialState(
  message: ChatMessage,
  options: Required<ContentProcessorOptions>,
): ContentProcessorState {
  return {
    message,
    nodes: [],
    openRefs: [],
    deferredRefGroups: [],
    accumulatedRefIds: [],
    toolUses: {},
    toolUseStack: [],
    chunks: {
      start: [],
      debug: [],
      error: [],
      canceled: [],
      warning: [],
      image: [],
      artifact: [],
      a2ui: [],
      progress: [],
      ref: [],
      ref_group: [],
      ref_start: [],
      text: [],
      tool_usage_start: [],
      tool_usage_progress: [],
      tool_usage_stop: [],
      msg_ids: [],
      paused: [],
    },
    options,
  };
}

/** Process a single event against a state, returning the updated state. */
function processEvent(
  state: ContentProcessorState,
  event: StreamingEvent,
  previousEvent: StreamingEvent | undefined,
): ContentProcessorState {
  let nextState = addPreviousEventFormatting(state, event, previousEvent);

  if (isReferenceStartEvent(event))
    return handleReferenceStartEvent(nextState, event);
  if (isReferenceGroupEvent(event))
    return handleReferenceGroupEvent(nextState, event);
  if (isReferenceEvent(event)) return handleReferenceEvent(nextState, event);
  if (isTextEvent(event)) return handleTextEvent(nextState, event);

  nextState = flushAccumulatedRefs(nextState);

  if (isDebugEvent(event)) return handleDebugEvent(nextState, event);
  if (isErrorEvent(event)) return handleErrorEvent(nextState, event);
  if (isCanceledEvent(event)) return handleCanceledEvent(nextState, event);
  if (isImageEvent(event)) return handleImageEvent(nextState, event);
  if (isArtifactEvent(event)) return handleArtifactEvent(nextState, event);
  if (isA2UIEvent(event)) return handleA2UIEvent(nextState, event);
  if (isProgressEvent(event)) return handleProgressEvent(nextState, event);
  if (isToolUsageStartEvent(event))
    return handleToolUsageStartEvent(nextState, event);
  if (isToolUsageProgressEvent(event))
    return handleToolUsageProgressEvent(nextState, event);
  if (isToolUsageStopEvent(event))
    return handleToolUsageStopEvent(nextState, event);
  if (isWarningEvent(event)) return handleWarningEvent(nextState, event);

  return { ...nextState };
}

/**
 * Apply new events to a prior state without building the rendered content
 * segments. Returns the advanced processor state plus the categorized chunks.
 * Callers that produce their own (cloned) segments — e.g. the streaming store,
 * which deep-clones nodes for stable React references — use this to avoid a
 * redundant `preprocessContentNodes` pass over the full node tree on every push.
 *
 * The caller provides the previousEvent (last event processed before this batch)
 * so that addPreviousEventFormatting works correctly for the first new event.
 */
export function applyContentEvents(
  state: ContentProcessorState,
  newEvents: StreamingEvent[],
  previousEvent: StreamingEvent | undefined,
): {
  chunks: StreamingEventCollection;
  state: ContentProcessorState;
} {
  let current = newEvents.reduce((s, event, index) => {
    const prev = index === 0 ? previousEvent : newEvents[index - 1];
    return processEvent(s, event, prev);
  }, state);

  current = flushAccumulatedRefs(current);
  current = validateResult(current);

  return {
    chunks: current.chunks,
    state: current,
  };
}

/**
 * Process only new events starting from a prior state, returning the rendered
 * content segments alongside the advanced state. A thin wrapper over
 * {@link applyContentEvents} that adds the `preprocessContentNodes` pass.
 */
export function buildContentIncremental(
  state: ContentProcessorState,
  newEvents: StreamingEvent[],
  previousEvent: StreamingEvent | undefined,
): {
  content: ContentSegment[];
  chunks: StreamingEventCollection;
  state: ContentProcessorState;
} {
  const { chunks, state: current } = applyContentEvents(
    state,
    newEvents,
    previousEvent,
  );

  return {
    content: preprocessContentNodes(current.nodes),
    chunks,
    state: current,
  };
}

/**
 * Content processor — processes all events from scratch.
 */
export function buildContent(
  message: ChatMessage,
  { isDebugMode = false, isStreaming = false }: ContentProcessorOptions = {},
): { content: ContentSegment[]; chunks: StreamingEventCollection } {
  const initial = createInitialState(message, { isDebugMode, isStreaming });
  const result = buildContentIncremental(initial, message.events, undefined);
  return {
    content: result.content,
    chunks: result.chunks,
  };
}
